import React from 'react';
import styled from '@emotion/styled';
import { Color } from '../../../features/shared/styles/Color';

interface ILoaderProps {
	color?: string;
	colorFadePercentage?: number;
}

// Function to darken a color
function darkenColor(color: string, percent: number) {
	// Parse the color into RGB components
	const r = parseInt(color.slice(1, 3), 16);
	const g = parseInt(color.slice(3, 5), 16);
	const b = parseInt(color.slice(5, 7), 16);

	// Calculate the darkened RGB components
	const newR = Math.max(0, r - Math.round(255 * (percent / 100)));
	const newG = Math.max(0, g - Math.round(255 * (percent / 100)));
	const newB = Math.max(0, b - Math.round(255 * (percent / 100)));

	// Convert the darkened RGB components back to a hex color
	const newColor = `#${newR.toString(16).padStart(2, '0')}${newG
		.toString(16)
		.padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`;

	return newColor;
}

export const Loader = (props: ILoaderProps) => {
	// eslint-disable-next-line react/destructuring-assignment
	const color = props.color || '#ffffff80';
	const { colorFadePercentage } = props;
	const colorFade = darkenColor(
		color || '#ffffff80',
		colorFadePercentage ?? 10,
	);
	return (
		<StyledLoader
			colorFade={colorFade}
			color={color}
			data-testid="loader"
			className="loader"
		/>
	);
};

export const LoaderPage = () => (
	<StyledLoaderPage>
		<Loader colorFadePercentage={20} color={Color.PrimaryDefault} />
	</StyledLoaderPage>
);

const StyledLoaderPage = styled.div`
	display: flex;
	justify-content: center;
	align-items: center;
	height: 100vh;
`;

const StyledLoader = styled.span<{ color: string; colorFade: string }>`
	width: 24px;
	height: 24px;
	display: grid;
	border-radius: 50%;
	mask: radial-gradient(farthest-side, #0000 40%, #000 41%);
	background: linear-gradient(
				0deg,
				${(props) => props.color || '#ffffff80'} 50%,
				/* Use the provided color or default to #ffffff80 */
					${(props) => props.colorFade || '#ffffffff'} 0
					/* Use the generated colorFade or default to #ffffffff */
			)
			center/2px 100%,
		linear-gradient(
				90deg,
				${(props) => props.color || '#ffffff40'} 50%,
				/* Use the provided color or default to #ffffff40 */
					${(props) => props.colorFade || '#ffffffbf'} 0
					/* Use the generated colorFade or default to #ffffffbf */
			)
			center/100% 2px;
	background-repeat: no-repeat;
	animation: s3 1s infinite steps(12);

	&::before,
	&::after {
		content: '';
		grid-area: 1/1;
		border-radius: 50%;
		background: inherit;
		opacity: 0.9;
		transform: rotate(30deg);
	}

	&::after {
		opacity: 0.83;
		transform: rotate(60deg);
	}

	@keyframes s3 {
		100% {
			transform: rotate(1turn);
		}
	}
`;
